/**
 * Parses the copy-paste import format:
 *
 *   DE;FR
 *   ==Lernziel 01
 *   Haus;maison
 *   Hund;chien
 *   ==Lernziel 02
 *   Auto;voiture
 *
 * The first non-empty line holds the language codes (own language first), a line
 * starting with "==" opens a learning goal, every other line holds one word pair.
 * Pairs before the first "==" belong to no learning goal.
 */
import type { Card, VocabSet } from './types';
import { makeCardId } from './ids';

/** Marks a learning goal inside the import text. */
const GROUP_PREFIX = '==';

export type ParseIssue = {
  line: number;
  text: string;
  reason: string;
};

export type ParseResult = {
  nativeLang: string;
  foreignLang: string;
  cards: Card[];
  issues: ParseIssue[];
};

const SEPARATORS = [';', '\t', '|', ','];

/** Picks the separator that splits the header line into exactly two parts. */
function detectSeparator(headerLine: string): string {
  for (const sep of SEPARATORS) {
    if (headerLine.split(sep).length === 2) return sep;
  }
  for (const sep of SEPARATORS) {
    if (headerLine.includes(sep)) return sep;
  }
  return ';';
}

function isSkippable(line: string): boolean {
  const trimmed = line.trim();
  return trimmed === '' || trimmed.startsWith('#') || trimmed.startsWith('//');
}

export function parseImport(input: string): ParseResult {
  const rawLines = input.replace(/\r\n?/g, '\n').split('\n');
  const issues: ParseIssue[] = [];
  const cards: Card[] = [];
  const seen = new Set<string>();

  let headerIndex = -1;
  for (let i = 0; i < rawLines.length; i++) {
    if (!isSkippable(rawLines[i])) {
      headerIndex = i;
      break;
    }
  }

  if (headerIndex === -1) {
    return {
      nativeLang: '',
      foreignLang: '',
      cards: [],
      issues: [{ line: 1, text: '', reason: 'Der Text ist leer.' }],
    };
  }

  const separator = detectSeparator(rawLines[headerIndex]);
  const headerParts = rawLines[headerIndex].split(separator).map((p) => p.trim());

  if (headerParts.length !== 2 || !headerParts[0] || !headerParts[1]) {
    issues.push({
      line: headerIndex + 1,
      text: rawLines[headerIndex],
      reason: `Die erste Zeile muss zwei Sprachen enthalten, z.B. "DE${separator}FR".`,
    });
  }

  const nativeLang = (headerParts[0] || 'DE').toUpperCase();
  const foreignLang = (headerParts[1] || 'FR').toUpperCase();
  let group = '';

  for (let i = headerIndex + 1; i < rawLines.length; i++) {
    const raw = rawLines[i];
    if (isSkippable(raw)) continue;

    const trimmed = raw.trim();
    if (trimmed.startsWith(GROUP_PREFIX)) {
      const name = trimmed.slice(GROUP_PREFIX.length).trim();
      if (name === '') {
        issues.push({
          line: i + 1,
          text: raw,
          reason: `Dem "${GROUP_PREFIX}" fehlt der Name des Lernziels.`,
        });
      }
      group = name;
      continue;
    }

    const parts = raw.split(separator).map((p) => p.trim());

    if (parts.length < 2) {
      issues.push({
        line: i + 1,
        text: raw,
        reason: `Kein "${separator}" gefunden - die Zeile wird übersprungen.`,
      });
      continue;
    }
    if (parts.length > 2) {
      issues.push({
        line: i + 1,
        text: raw,
        reason: `Mehr als ein "${separator}" - nur die ersten zwei Teile werden verwendet.`,
      });
    }

    const native = parts[0];
    const foreign = parts[1];

    if (!native || !foreign) {
      issues.push({ line: i + 1, text: raw, reason: 'Ein Wort fehlt.' });
      continue;
    }

    const id = makeCardId(native, foreign, group);
    if (seen.has(id)) {
      issues.push({ line: i + 1, text: raw, reason: 'Doppeltes Wortpaar - übersprungen.' });
      continue;
    }
    seen.add(id);

    cards.push({
      id,
      native,
      foreign,
      group,
      box: 1,
      asked: 0,
      correct: 0,
      lastAsked: null,
    });
  }

  if (cards.length === 0) {
    issues.push({ line: headerIndex + 1, text: '', reason: 'Es wurde kein Wortpaar gefunden.' });
  }

  return { nativeLang, foreignLang, cards, issues };
}

/**
 * Counterpart of `parseImport`: renders a set back into the text format, so the
 * edit screen shows exactly what was imported, learning goals included.
 */
export function formatImport(set: VocabSet): string {
  const lines = [`${set.nativeLang};${set.foreignLang}`];
  let group: string | null = null;

  for (const card of set.cards) {
    if (card.group !== group) {
      group = card.group;
      if (group !== '') lines.push(`${GROUP_PREFIX}${group}`);
    }
    lines.push(`${card.native};${card.foreign}`);
  }

  return lines.join('\n');
}
